import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { firstValueFrom } from 'rxjs';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { DailyScheduleConfig } from '../daily-schedule/daily-schedule.interface';
import { JourneyConfig } from '../journey/journey.interface';
import { WorkCycleConfig, WorkCycleDayType } from './work-cycle.interface';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-work-cycle',
  standalone: true,
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule, MatCardModule],
  templateUrl: './work-cycle.component.html',
  styleUrl: './work-cycle.component.scss',
})
export class WorkCycleComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  dataSource = new MatTableDataSource<WorkCycleConfig>([]);
  allCycles: WorkCycleConfig[] = [];
  companies: CompaniesRecord[] = [];
  journeys: JourneyConfig[] = [];
  schedules: DailyScheduleConfig[] = [];
  isLoading = true;
  actions: TableAction<WorkCycleConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<WorkCycleConfig>[] = [];
  activeFilters: Record<string, string | number | boolean | null> = {};
  activeSearch = '';

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.configureTranslations();
        this.dataSource.data = this.dataSource.data.map((cycle) => this.normalizeCycle(cycle));
        this.buildTableFilters();
        this.applyTableFilters();
      });
    void this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    try {
      const [cycles, companiesResponse, journeys, schedules] = await Promise.all([
        firstValueFrom(this.apiService.get<WorkCycleConfig[]>('labor-config/work-cycles')),
        this.companyDirectory.getAllActive(),
        firstValueFrom(this.apiService.get<JourneyConfig[]>('labor-config/journeys')),
        firstValueFrom(this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules')),
      ]);

      this.companies = this.normalizeCompanies(companiesResponse)
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.journeys = journeys || [];
      this.schedules = schedules || [];
      this.allCycles = (cycles || []).map((cycle) => this.normalizeCycle(cycle));
      this.buildTableFilters();
      this.applyTableFilters();
    } catch (error) {
      console.error('Error loading work cycles', error);
      this.allCycles = [];
      this.dataSource.data = [];
      this.companies = [];
      this.journeys = [];
      this.schedules = [];
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('workCyclesCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/work-cycles/new']);
  }

  async handleEdit(row: WorkCycleConfig): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/work-cycles', row.workCycleId, 'edit'], {
      queryParams: row.companiesRecordId
        ? { companyId: row.companiesRecordId }
        : undefined,
    });
  }

  async handleView(row: WorkCycleConfig): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/work-cycles', row.workCycleId], {
      queryParams: row.companiesRecordId
        ? { companyId: row.companiesRecordId }
        : undefined,
    });
  }

  handleDelete(row: WorkCycleConfig): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('workCyclesCatalog.dialog.deleteTitle'),
        message: this.t('workCyclesCatalog.dialog.deleteMessage', { cycle: row.name }),
        confirmLabel: this.t('workCyclesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`labor-config/work-cycles/${row.workCycleId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('workCyclesCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting work cycle', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('workCyclesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: WorkCycleConfig[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`labor-config/work-cycles/${row.workCycleId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting work cycles', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('workCyclesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onSearchChange(search: string): void {
    this.activeSearch = search.trim().toLowerCase();
    this.applyTableFilters();
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.applyTableFilters();
  }

  private buildTableFilters(): void {
    this.tableFilters = [{
      id: 'company',
      label: this.t('orgUnitsCatalog.fields.company'),
      icon: 'business',
      searchable: true,
      value: this.activeFilters['company'] ?? null,
      options: this.companies.map((company) => ({
        value: company.companiesRecordId,
        label: company.name,
        searchText: `${company.code || ''} ${company.center || ''}`.trim(),
      })),
    }];
  }

  private applyTableFilters(): void {
    const companyId = this.activeFilters['company'];
    this.dataSource.data = this.allCycles.filter((cycle) => {
      const matchesCompany = !companyId || cycle.companiesRecordId === companyId;
      const matchesSearch = !this.activeSearch ||
        [cycle.name, cycle.description, cycle.companyName, cycle.workPatternLabel, cycle.activeLabel]
          .join(' ')
          .toLowerCase()
          .includes(this.activeSearch);
      return matchesCompany && matchesSearch;
    });
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('workCyclesCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'workCycles', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: { resource: 'workCycles', action: 'read' },
        behavior: 'refresh',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('common.select'),
        colorClass: 'info',
        auth: { resource: 'workCycles', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: { resource: 'workCycles', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: { resource: 'workCycles', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('workCyclesCatalog.fields.name'), type: 'text', visible: true },
      { def: 'description', label: this.t('workCyclesCatalog.fields.description'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('orgUnitsCatalog.fields.company'), type: 'text', visible: true },
      { def: 'workPatternLabel', label: this.t('workCyclesCatalog.fields.workPattern'), type: 'text', visible: true },
      {
        def: 'activeLabel',
        label: this.t('workCyclesCatalog.fields.status'),
        type: 'status',
        visible: true,
      },
    ];

    this.dataSource.filterPredicate = (cycle, filter) =>
      [cycle.name, cycle.description, cycle.companyName, cycle.workPatternLabel, cycle.activeLabel]
        .join(' ')
        .toLowerCase()
        .includes(filter);
  }

  private normalizeCycle(cycle: Partial<WorkCycleConfig>): WorkCycleConfig {
    const days = (cycle.days || []).map((day, index) => ({
      position: this.getDayPosition(day, index),
      dayType: (day.dayType || 'rest') as WorkCycleDayType,
      journeyId: day.journeyId,
      dailyScheduleId: day.dailyScheduleId,
    }));
    const cycleLength = Number(cycle.cycleLength || days.length || 0);
    const isActive = cycle.isActive !== false;

    return {
      workCycleId: cycle.workCycleId || crypto.randomUUID(),
      center: cycle.center || '',
      companiesRecordId: cycle.companiesRecordId,
      name: cycle.name || '',
      description: cycle.description || '',
      cycleLength,
      repeat: { mode: 'indefinite' },
      days,
      isActive,
      companyName: this.getCompanyName(cycle.companiesRecordId),
      cycleLengthLabel: this.t('workCyclesCatalog.labels.cycleLength', { count: cycleLength }),
      workPatternLabel: this.getWorkPatternLabel(days, cycleLength),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private getCompanyName(companiesRecordId: string | null | undefined): string {
    if (!companiesRecordId) {
      return this.t('workCyclesCatalog.labels.noCompany');
    }

    return (
      this.companies.find((company) => company.companiesRecordId === companiesRecordId)?.name ||
      this.t('workCyclesCatalog.labels.noCompany')
    );
  }

  private getWorkPatternLabel(days: WorkCycleConfig['days'], cycleLength: number): string {
    const workDays = days.filter((day) => day.dayType === 'work').length;
    const restDays = Math.max(cycleLength - workDays, 0);
    return this.t('workCyclesCatalog.labels.workPattern', {
      work: workDays,
      rest: restDays,
    });
  }

  private getDayPosition(day: WorkCycleConfig['days'][number], index: number): number {
    const legacyDay = day as WorkCycleConfig['days'][number] & { dayOrder?: number };
    return Number(day.position || legacyDay.dayOrder || index + 1);
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'workCycles', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
